// ✅ src/screens/LikesScreen.js (FULL)
// - Shows inbound likes (people who liked you)
// - ❌ Reject deletes inbound like
// - ❤️ Like back creates my like + creates/ensures match + opens chat
// ✅ NEW: sends Firestore notification to the other person when you LIKE BACK (and optional MATCH)
// ✅ UPDATE: removed "No likes yet." UI completely

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import useSubscription from "../hooks/useSubscription";
import { createNotification } from "../lib/notify";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function calcAgeFromBirthDate(birthDateTs) {
  const ms = msFromTs(birthDateTs);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function makeChatId(a, b) {
  const x = safeStr(a);
  const y = safeStr(b);
  if (!x || !y) return "";
  return [x, y].sort().join("__");
}
function makeLikeId(fromUid, toUid) {
  const a = safeStr(fromUid);
  const b = safeStr(toUid);
  return a && b ? `${a}__${b}` : "";
}

/* ---------------- UI ---------------- */
function SwipeLikeCard({ person, swipeOut, onOpenProfile }) {
  const name = safeStr(person?.displayName) || "Unknown";
  const avatar = safeStr(person?.avatar) || safeStr(person?.photos?.[0]);
  const age = Number.isFinite(Number(person?.age)) ? Number(person.age) : null;
  const city = safeStr(person?.city);
  const goal = safeStr(person?.relationshipGoal);

  const animStyle =
    swipeOut === "right" ? S.cardSwipeRight : swipeOut === "left" ? S.cardSwipeLeft : null;

  return (
    <button
      type="button"
      style={S.bigCardBtn}
      onClick={onOpenProfile}
      aria-label={`Open ${name}'s profile`}
      title="View profile"
    >
      <div style={{ ...S.bigCard, ...(animStyle || null) }}>
        <div style={S.bigMedia}>
          {avatar ? (
            <img src={avatar} alt={name} style={S.bigImg} />
          ) : (
            <div style={S.bigFallback}>{initials(name)}</div>
          )}

          <div style={S.overlay}>
            <div style={S.nameRow}>
              <span style={S.name}>{name}</span>
              {age !== null ? <span style={S.age}>· {age}</span> : null}
            </div>

            {city ? <div style={S.metaLine}>📍 {city}</div> : null}
            {goal ? <div style={S.goalPill}>🔥 {goal}</div> : null}
          </div>
        </div>
      </div>
    </button>
  );
}

function UpgradeOverlay({ plan, onUpgrade, onBack }) {
  return (
    <div style={S.gateWrap} role="dialog" aria-modal="true">
      <div style={S.gateCard}>
        <div style={S.gateTitle}>Please upgrade</div>
        <div style={S.gateText}>
          Likes is a premium feature.
          <div style={{ marginTop: 6, opacity: 0.8 }}>
            Current plan: <b>{plan || "Free"}</b>
          </div>
        </div>

        <div style={S.gateBtns}>
          <button type="button" style={S.gateBtnPrimary} onClick={onUpgrade}>
            Upgrade now
          </button>
          <button type="button" style={S.gateBtnGhost} onClick={onBack}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- main ---------------- */
export default function LikesScreen() {
  const nav = useNavigate();

  // ✅ SUBSCRIPTION GATE
  const { loading: subLoading, allowed, plan, err } = useSubscription();
  const gated = !subLoading && !allowed;

  const [myUid, setMyUid] = useState("");
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [acting, setActing] = useState(false);
  const [swipeOut, setSwipeOut] = useState(""); // "" | "left" | "right"

  const current = deck[index] || null;
  const countLabel = useMemo(() => `${deck.length}`, [deck.length]);

  async function hydratePeople(ids) {
    const uniq = Array.from(new Set((ids || []).filter(Boolean))).slice(0, 240);
    const results = [];

    for (const uid of uniq) {
      try {
        const snap = await getDoc(doc(db, "datingProfiles", uid));
        if (!snap.exists()) {
          results.push({ uid, displayName: "Unknown", avatar: "", photos: [], age: null, city: "", relationshipGoal: "" });
          continue;
        }
        const d = snap.data();
        const computed = calcAgeFromBirthDate(d?.birthDate);
        const age = Number.isFinite(Number(d?.age)) ? Number(d.age) : computed;

        results.push({
          uid,
          displayName: safeStr(d.displayName) || "Unknown",
          avatar: safeStr(d.avatar) || safeStr(d.photos?.[0]),
          photos: Array.isArray(d.photos) ? d.photos : [],
          age: Number.isFinite(Number(age)) ? Number(age) : null,
          city: safeStr(d.city),
          relationshipGoal: safeStr(d.relationshipGoal),
        });
      } catch {
        results.push({ uid, displayName: "Unknown", avatar: "", photos: [], age: null, city: "", relationshipGoal: "" });
      }
    }
    return results;
  }

  async function loadLikes(uid) {
    try {
      const likesSnap = await getDocs(
        query(collection(db, "likes"), where("toUid", "==", uid), limit(200))
      );
      const fromIds = likesSnap.docs.map((d) => safeStr(d.data()?.fromUid)).filter(Boolean);

      const people = await hydratePeople(fromIds);
      setDeck(people);
      setIndex(0);
    } catch (e) {
      console.error("[LIKES] load error:", e);
      setDeck([]);
      setIndex(0);
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u?.uid) {
        nav("/login", { replace: true });
        return;
      }
      setMyUid(u.uid);
      loadLikes(u.uid);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPublicProfile = (uid) => {
    const id = safeStr(uid);
    if (!id) return;
    nav(`/public-profile/${id}`);
  };

  async function animateThenNext(dir, fn) {
    if (!current || acting) return;
    setActing(true);
    setSwipeOut(dir);

    try {
      await fn?.();
    } catch (e) {
      console.error("likes action error", e);
    }

    setTimeout(() => {
      setIndex((i) => i + 1);
      setSwipeOut("");
      setActing(false);
    }, 260);
  }

  async function onReject() {
    const u = auth.currentUser;
    if (!u?.uid || !current?.uid) return;

    const my = u.uid;
    const other = safeStr(current.uid);

    await animateThenNext("left", async () => {
      await runTransaction(db, async (tx) => {
        const inboundId = makeLikeId(other, my);
        tx.delete(doc(db, "likes", inboundId));

        const swipeRef = doc(db, "datingProfiles", my, "swipes", other);
        tx.set(swipeRef, { action: "pass", createdAt: serverTimestamp() }, { merge: true });
      });
    });
  }

  async function onLikeBackOpenChat() {
    const u = auth.currentUser;
    if (!u?.uid || !current?.uid) return;

    const my = u.uid;
    const other = safeStr(current.uid);

    await animateThenNext("right", async () => {
      const chatId = makeChatId(my, other);

      let becameMatch = false;

      await runTransaction(db, async (tx) => {
        const myLikeId = makeLikeId(my, other);
        const inboundId = makeLikeId(other, my);

        const myLikeRef = doc(db, "likes", myLikeId);
        const inboundRef = doc(db, "likes", inboundId);
        const inboundSnap = await tx.get(inboundRef);

        // I'm liking them back
        tx.set(
          myLikeRef,
          { createdAt: serverTimestamp(), fromUid: my, toUid: other, isSuperLike: false },
          { merge: true }
        );

        // If inbound exists, this is a match
        if (inboundSnap.exists()) {
          tx.delete(inboundRef);
          becameMatch = true;
        }

        const matchRef = doc(db, "matches", chatId);
        tx.set(
          matchRef,
          {
            createdAt: serverTimestamp(),
            isActive: true,
            lastMessageAt: serverTimestamp(),
            lastMessageText: "",
            unreadCountA: 0,
            unreadCountB: 0,
            userA: my,
            userB: other,
          },
          { merge: true }
        );

        const swipeRef = doc(db, "datingProfiles", my, "swipes", other);
        tx.set(swipeRef, { action: "like", createdAt: serverTimestamp() }, { merge: true });
      });

      // ✅ SEND NOTIFICATION: they got liked back
      try {
        await createNotification(other, {
          type: "LIKE",
          title: "Someone liked you ❤️",
          message: "You got a new like back. Open Matches to chat 😉",
          data: { fromUid: my, matchId: chatId },
        });
      } catch (e) {
        console.error("notify like-back failed:", e);
      }

      // ✅ OPTIONAL: also send a MATCH notification (nice)
      if (becameMatch) {
        try {
          await createNotification(other, {
            type: "MATCH",
            title: "It’s a match 🎉",
            message: "You matched! Open Matches to start chatting.",
            data: { fromUid: my, matchId: chatId },
          });
        } catch (e) {
          console.error("notify match failed:", e);
        }
      }

      nav(`/chat/${chatId}`);
    });
  }

  return (
    <div style={S.stage}>
      {!subLoading && err ? (
        <div style={S.warnBox}>
          <div style={S.warnTitle}>Subscription read error</div>
          <div style={S.warnText}>{err}</div>
        </div>
      ) : null}

      <div style={{ ...S.content, ...(gated ? S.blurred : null) }}>
        <div style={S.headerRow}>
          <div style={S.headerLeft}>
            <div style={S.title}>Likes</div>
            <div style={S.subTitle}>({countLabel})</div>
          </div>

          <button type="button" style={S.refreshBtn} onClick={() => myUid && loadLikes(myUid)}>
            ♻️ Refresh
          </button>
        </div>

        {/* ✅ Removed "No likes yet." UI completely */}
        {!current ? null : (
          <>
            <SwipeLikeCard
              person={current}
              swipeOut={swipeOut}
              onOpenProfile={() => openPublicProfile(current.uid)}
            />

            <div style={S.actions}>
              <button
                type="button"
                style={S.noBtn}
                onClick={onReject}
                disabled={acting}
                aria-label="Reject"
                title="Reject"
              >
                ❌
              </button>

              <button
                type="button"
                style={S.likeBtn}
                onClick={onLikeBackOpenChat}
                disabled={acting}
                aria-label="Like back"
                title="Like back"
              >
                ❤️
              </button>
            </div>

            <div style={S.hint}>Tip: Tap the card to view profile • ❤️ like back • ❌ reject</div>
          </>
        )}
      </div>

      {gated ? <UpgradeOverlay plan={plan} onUpgrade={() => nav("/upgrade")} onBack={() => nav(-1)} /> : null}

      {subLoading ? <div style={S.loadingFloat}>Checking subscription…</div> : null}
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  stage: { position: "relative", display: "grid", gap: 14, justifyItems: "center", padding: 12 },

  content: { width: "100%", display: "grid", gap: 14, justifyItems: "center" },
  blurred: { filter: "blur(10px)", transform: "scale(0.98)", opacity: 0.75, pointerEvents: "none" },

  headerRow: {
    width: "min(640px, 100%)",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  headerLeft: { display: "flex", gap: 8, alignItems: "baseline" },
  title: { fontWeight: 1000, fontSize: 18 },
  subTitle: { opacity: 0.7, fontWeight: 900, fontSize: 13 },

  refreshBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
  },

  bigCardBtn: { padding: 0, border: "none", background: "transparent", cursor: "pointer", width: "min(640px, 100%)" },
  bigCard: {
    width: "min(640px, 100%)",
    borderRadius: 28,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 22px 70px rgba(2,6,23,0.10)",
    overflow: "hidden",
    transition: "transform 240ms ease, opacity 240ms ease",
    willChange: "transform, opacity",
  },
  cardSwipeRight: { transform: "translateX(120%) rotate(10deg)", opacity: 0.0 },
  cardSwipeLeft: { transform: "translateX(-120%) rotate(-10deg)", opacity: 0.0 },

  bigMedia: { position: "relative" },
  bigImg: { width: "100%", height: 520, objectFit: "cover", display: "block" },
  bigFallback: {
    width: "100%",
    height: 520,
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.85)",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 44,
  },

  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 18,
    background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.45) 40%, rgba(0,0,0,.85) 100%)",
    color: "#fff",
  },
  nameRow: { display: "flex", alignItems: "baseline", gap: 8 },
  name: { fontWeight: 1000, fontSize: 24, letterSpacing: -0.5 },
  age: { fontWeight: 900, fontSize: 20, opacity: 0.85 },
  metaLine: { marginTop: 6, fontWeight: 900, fontSize: 14, opacity: 0.9 },

  goalPill: {
    marginTop: 10,
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 12,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.28)",
    backdropFilter: "blur(10px)",
  },

  actions: { display: "flex", gap: 16, alignItems: "center", justifyContent: "center", marginTop: 2 },
  noBtn: {
    width: 68,
    height: 68,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    cursor: "pointer",
    fontSize: 26,
    fontWeight: 1000,
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.78)",
  },
  likeBtn: {
    width: 68,
    height: 68,
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    fontSize: 28,
    fontWeight: 1000,
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    color: "#fff",
  },

  hint: { fontWeight: 900, opacity: 0.65, fontSize: 13, textAlign: "center", paddingBottom: 6 },

  gateWrap: { position: "fixed", inset: 0, display: "grid", placeItems: "center", padding: 14, background: "rgba(2,6,23,0.45)", zIndex: 9999 },
  gateCard: { width: "min(520px, 100%)", borderRadius: 22, border: "1px solid rgba(2,6,23,0.10)", background: "rgba(255,255,255,0.92)", boxShadow: "0 30px 90px rgba(2,6,23,0.22)", padding: 14 },
  gateTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.2 },
  gateText: { marginTop: 8, fontWeight: 900, opacity: 0.75, lineHeight: 1.35 },
  gateBtns: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" },
  gateBtnPrimary: { border: "none", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, color: "#fff", background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))" },
  gateBtnGhost: { border: "1px solid rgba(2,6,23,0.10)", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, background: "rgba(255,255,255,0.80)" },

  loadingFloat: { position: "fixed", bottom: 14, left: "50%", transform: "translateX(-50%)", padding: "10px 12px", borderRadius: 999, border: "1px solid rgba(2,6,23,0.10)", background: "rgba(255,255,255,0.92)", fontWeight: 1000, boxShadow: "0 14px 34px rgba(2,6,23,0.18)", zIndex: 9999 },

  warnBox: { width: "min(640px, 100%)", borderRadius: 16, border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.07)", padding: 12 },
  warnTitle: { fontWeight: 1000, marginBottom: 4 },
  warnText: { fontWeight: 900, opacity: 0.8, fontSize: 13, wordBreak: "break-word" },
};